//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { getAllAlphabetizedGalleries } from '@galleries/selectors/getAllAlphabetizedGalleries';
import {
  GALLERY_DISPLAY_TYPES_LIST,
  getGalleriesSubItems,
  IInsightsSidebarPage,
  INSIGHTS_ROUTES,
  INSIGHTS_SIDEBAR_PAGES,
  PageType,
} from '@insights/insightsSidebarConstants';
import { createSelector } from 'reselect';
import { getFeatureNames } from 'app/directives/fanreel-product-name/utils';
import getGalleriesClientType, {
  GalleriesClientType,
} from '@galleries/selectors/getGalleriesClientType';
import { ICuralateStore } from '@models/AppState';
import { getTikTokLike2BuyStore } from '@tiktok/redux/like2buy/selectors/getTikTokLike2Buy';
import { isDefined } from '@utils/filterPredicates';
import { FeatureNames } from '@bvengineering/curalate-features';
import { hasItemAccessByStateName } from 'app/initialization/routing/hasItemAccessByStateName';
import { createHasFeatureSelector } from '@redux/selectors/createHasFeatureSelector';
import { getIsSocialDashboardsEnabled } from '@account-settings/redux/accountSettings/selectors/getIsSocialDashboardsEnabled';
import { getIsListeningEnabled } from '@listening/selectors/getIsListeningEnabled';
import { getIsContentSupplyChainEnabled } from '@insights/redux/selectors/getIsContentSupplyChainEnabled';

export const getShouldBreakdownByDisplayType = createSelector(
  (state: ICuralateStore) => state.session.features.galleries_custom,
  (state: ICuralateStore) =>
    state.session.features.insights_custom_galleries_breakdown_improvements,
  (isCustomGalleryClient, isCustomGalleryBreakdownImprovementsEnabled) => {
    return (
      isCustomGalleryClient && !isCustomGalleryBreakdownImprovementsEnabled
    );
  },
);

export const getIsLike2BuyEnabled = createHasFeatureSelector(
  FeatureNames.Like2Buy,
);
export const getIsLike2BuyTiktokEnabled = createHasFeatureSelector(
  FeatureNames.Like2BuyTiktok,
);

const getAvailableInsightsPages = createSelector(
  getAllAlphabetizedGalleries,
  getGalleriesClientType,
  getShouldBreakdownByDisplayType,
  getTikTokLike2BuyStore,
  getIsLike2BuyEnabled,
  getIsLike2BuyTiktokEnabled,
  getIsSocialDashboardsEnabled,
  getIsListeningEnabled,
  getIsContentSupplyChainEnabled,
  (
    allGalleries,
    galleriesClientType,
    shouldBreakdownByDisplayType,
    tiktokLike2BuyStore,
    isLike2BuyEnabled,
    isLike2BuyTiktokEnabled,
    isSocialDashboardsEnabled,
    isListeningEnabled,
    isContentSupplyChainEnabled,
  ) => {
    // Pull galleries title and subItems from redux
    const galleriesPage = INSIGHTS_SIDEBAR_PAGES.GALLERIES;
    if (isDefined(galleriesPage)) {
      galleriesPage.title = getFeatureNames(galleriesClientType).default;
      if (shouldBreakdownByDisplayType) {
        galleriesPage.subItems = GALLERY_DISPLAY_TYPES_LIST.map(
          (displayType) => {
            return {
              sref: INSIGHTS_ROUTES.GALLERIES_DETAIL,
              title: displayType.label,
              params: {
                pageType: PageType.DISPLAY,
                pageSource: displayType.value,
              },
            };
          },
        );
      } else if (
        galleriesClientType !== GalleriesClientType.instashop ||
        allGalleries.length > 1
      ) {
        galleriesPage.subItems = getGalleriesSubItems(allGalleries);
      }
    }

    const like2BuyPage = INSIGHTS_SIDEBAR_PAGES.LIKE2BUY;
    if (isDefined(like2BuyPage)) {
      like2BuyPage.subItems = [];
      if (isLike2BuyEnabled) {
        like2BuyPage.subItems.push({
          sref: INSIGHTS_ROUTES.LIKE2BUY,
          title: 'Instagram',
          params: {},
        });
      }
      if (isLike2BuyTiktokEnabled && isDefined(tiktokLike2BuyStore)) {
        like2BuyPage.subItems.push({
          sref: INSIGHTS_ROUTES.LIKE2BUY_V2,
          title: 'TikTok',
          params: {
            storeId: tiktokLike2BuyStore.id,
          },
        });
      }
      like2BuyPage.state = INSIGHTS_ROUTES.LIKE2BUY_SUMMARY;
    }

    // Create list of navItems filtered to only include those with access
    let navItemsWithAccess = [
      INSIGHTS_SIDEBAR_PAGES.SUMMARY,
      ...[
        isSocialDashboardsEnabled
          ? INSIGHTS_SIDEBAR_PAGES.ANALYTICS
          : undefined,
        isListeningEnabled ? INSIGHTS_SIDEBAR_PAGES.LISTENING : undefined,
        INSIGHTS_SIDEBAR_PAGES.CAMPAIGNS,
      ],
      galleriesPage,
      (like2BuyPage?.subItems?.length ?? 0) > 0 ? like2BuyPage : undefined,
      INSIGHTS_SIDEBAR_PAGES.REVEAL,
      INSIGHTS_SIDEBAR_PAGES.SHOWROOM,
      INSIGHTS_SIDEBAR_PAGES.SYNDICATION,
      INSIGHTS_SIDEBAR_PAGES.RIGHTS_REQUESTS,
      // TODO: PD-318575 - Remove once feature is released
      isContentSupplyChainEnabled
        ? INSIGHTS_SIDEBAR_PAGES.CONTENT_SUPPLY_CHAIN
        : undefined,
    ]
      .filter(isDefined)
      .filter(
        ({ state, title }) =>
          hasItemAccessByStateName(state) && title.trim() !== '',
      );

    // If we only have 1 actual page don't show the summary
    if (navItemsWithAccess.length === 2) {
      navItemsWithAccess = navItemsWithAccess
        .filter(isDefined)
        .filter(
          (navItemWithAccess: IInsightsSidebarPage) =>
            navItemWithAccess.state !== INSIGHTS_ROUTES.SUMMARY,
        );
    }

    return navItemsWithAccess;
  },
);

export const isInsightsPageVisible = createSelector(
  getAvailableInsightsPages,
  (pages) =>
    pages.filter(
      (page) =>
        page?.state !== INSIGHTS_ROUTES.SUMMARY &&
        // Hiding the Insights tab if Analytics, Listening, and Campaigns are the only pages available
        // since they are linkout pages only you would be immediately redirected.
        page?.state !== INSIGHTS_ROUTES.ANALYTICS &&
        page?.state !== INSIGHTS_ROUTES.LISTENING &&
        page?.state !== INSIGHTS_ROUTES.CAMPAIGNS,
    ).length > 0,
);

export default getAvailableInsightsPages;
